/**
 * useAnchorRect — tracks the on-screen rectangle of a `data-coach="<id>"` anchor.
 *
 * Shared by CoachOverlay (the LLM agent's single highlights) and
 * WalkthroughOverlay (the scripted tour). Returns the anchor's live
 * getBoundingClientRect (or null if it isn't on screen yet), re-measuring on
 * resize/scroll and a few short settle timers so it re-anchors correctly across
 * view transitions and React remounts (e.g. startup → project).
 */

import { useEffect, useState } from 'react';

export default function useAnchorRect(coachId) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!coachId) { setRect(null); return undefined; }

    const update = () => {
      const el = document.querySelector(`[data-coach="${CSS.escape(coachId)}"]`);
      setRect(el ? el.getBoundingClientRect() : null);
    };
    update();

    // Re-measure a few times: covers view transitions and cross-screen remounts
    // (the target may not exist on the very next frame after a navigation/load).
    const timers = [60, 200, 500].map((ms) => setTimeout(update, ms));
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [coachId]);

  return rect;
}
